import { DropDownExpiries } from '../../../reducer/options.reducer'
import { CallPutType } from '../../../reducer/multiStrikeChart.reducer'
import { getStrikeAndStrikeData } from '../../home/openInterestHeader/MultiStrikesMenu/MultiStrikesMenu'
import { findNearestNumber } from '../../../util/tradepanel'
import { getSecuritySnapshot } from '../../../selectors/security'

export type OverviewStrikeLeg = {
    strike: string
    callSecKey: string
    putSecKey: string
    callPut: CallPutType[]
}

/** Overview always previews the nearest real expiry; derived (synthetic) expiries are skipped. */
export function resolveOverviewExpiryName(
    optionsExpiries: DropDownExpiries[] | undefined
): string | undefined {
    if (!optionsExpiries?.length) return undefined
    return optionsExpiries.filter((o) => !o.derived)[0]?.expiry?.name
}

function isPlaceholderOptionChain(expiryData: DropDownExpiries): boolean {
    if (expiryData.strikes.length <= 1) {
        return expiryData.strikes[0]?.strike === 0 || expiryData.strikesArray.length <= 1
    }
    return false
}

/** Build strike legs for near-ATM preset (same spacing as multistrike Near ATM menu). */
export function resolveNearAtmStrikeLegs(
    expiryData: DropDownExpiries | undefined,
    underlyingToken: string,
    nearAtmPreset: number,
    priceMultiplier: number,
    roundingStrikesRequired: boolean,
    /** Strike-filter LTP when available — same signal multistrike uses for ATM. */
    underlyingLtpOverride?: number
): { atmStrike: number | undefined; legs: OverviewStrikeLeg[] } {
    if (!expiryData?.strikes?.length || isPlaceholderOptionChain(expiryData)) {
        return { atmStrike: undefined, legs: [] }
    }
    const underlyingLtp =
        underlyingLtpOverride ??
        getSecuritySnapshot(underlyingToken)?.lastTradedPrice ??
        0
    if (!underlyingLtp) {
        return { atmStrike: undefined, legs: [] }
    }
    const atmStrike = findNearestNumber(expiryData.strikesArray, underlyingLtp)
    const atmIndex = expiryData.strikes.findIndex((s) => s.strike === atmStrike)
    const atm = expiryData.strikes[atmIndex]
    if (!atm || atmIndex < 0) {
        return { atmStrike: undefined, legs: [] }
    }

    const legMap = new Map<string, OverviewStrikeLeg>()
    const addStrike = (dropDownStrike: typeof atm) => {
        const { strike, strikeData } = getStrikeAndStrikeData(
            dropDownStrike,
            priceMultiplier,
            roundingStrikesRequired
        )
        legMap.set(strike, {
            strike,
            callSecKey: strikeData.callSecKey,
            putSecKey: strikeData.putSecKey,
            callPut: strikeData.callPut,
        })
    }

    addStrike(atm)
    const sideSteps = [3, 5, 7, 9]
    sideSteps.forEach((step) => {
        if (nearAtmPreset < step) return
        const index = step === 3 ? 1 : step === 5 ? 2 : step === 7 ? 3 : 4
        const lower = expiryData.strikes[atmIndex - index]
        const upper = expiryData.strikes[atmIndex + index]
        if (lower) addStrike(lower)
        if (upper) addStrike(upper)
    })

    const legs = Array.from(legMap.values()).sort(
        (a, b) => Number(a.strike) - Number(b.strike)
    )
    return { atmStrike, legs }
}

export function atmStrikeLeg(
    expiryData: DropDownExpiries | undefined,
    underlyingToken: string,
    priceMultiplier: number,
    roundingStrikesRequired: boolean,
    underlyingLtpOverride?: number
): OverviewStrikeLeg | undefined {
    const { legs, atmStrike } = resolveNearAtmStrikeLegs(
        expiryData,
        underlyingToken,
        3,
        priceMultiplier,
        roundingStrikesRequired,
        underlyingLtpOverride
    )
    if (!legs.length || atmStrike == null) return undefined
    const label =
        priceMultiplier !== 100
            ? (atmStrike / priceMultiplier).toFixed(2)
            : String(atmStrike / priceMultiplier)
    return legs.find((l) => l.strike === label) ?? legs[Math.floor(legs.length / 2)]
}

export function uniqueSecKeysFromLegs(legs: OverviewStrikeLeg[], primaryKey: string): string[] {
    const keys = new Set<string>([primaryKey])
    legs.forEach((leg) => {
        keys.add(leg.callSecKey)
        keys.add(leg.putSecKey)
    })
    return Array.from(keys)
}
